import { useState } from 'react';
import { Search, ExternalLink, Loader, X, MapPin, AlertCircle } from 'lucide-react';
import { apiClient } from '../lib/apiClient';

export interface AspireProperty {
  PropertyID: number;
  PropertyName: string;
  PropertyAddress: string;
  ContactName: string;
  ContactCompany: string;
  ContactEmail: string;
  ContactPhone: string;
  Latitude: number | null;
  Longitude: number | null;
  Notes: string;
}

interface AspirePropertyPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (property: AspireProperty) => void;
}

export function AspirePropertyPicker({ isOpen, onClose, onSelect }: AspirePropertyPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [properties, setProperties] = useState<AspireProperty[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const params = new URLSearchParams();
      const trimmed = searchTerm.trim();
      if (trimmed) params.set('search', trimmed);
      const qs = params.toString();
      const path = qs ? `/api/integrations/aspire/properties?${qs}` : '/api/integrations/aspire/properties';
      const data = await apiClient.get<{ success: boolean; properties?: AspireProperty[] }>(path);
      setProperties(data.properties || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch properties from Aspire');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSelect = (property: AspireProperty) => {
    onSelect(property);
    setSearchTerm('');
    setProperties([]);
    setHasSearched(false);
    setError(null);
    onClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    setProperties([]);
    setHasSearched(false);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-surface rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
              <ExternalLink size={16} className="text-white" />
            </div>
            <h3 className="text-lg font-semibold text-theme-primary">Import from Aspire</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-subtle rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 border-b border-line">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-secondary" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search properties by name..."
                className="w-full pl-9 pr-3 py-2.5 border border-line rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-surface text-theme-primary"
                autoFocus
              />
            </div>
            <button
              onClick={handleSearch}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {loading ? <Loader size={16} className="animate-spin" /> : <Search size={16} />}
              Search
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="mx-6 mt-4 flex items-start gap-3 p-4 rounded-lg bg-red-50 border border-red-200 dark:bg-red-500/10 dark:border-red-500/30">
              <AlertCircle size={18} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3 text-theme-secondary">
                <Loader size={20} className="animate-spin" />
                <span className="text-sm">Searching Aspire properties...</span>
              </div>
            </div>
          )}

          {!loading && hasSearched && properties.length === 0 && !error && (
            <div className="flex flex-col items-center justify-center py-12 text-theme-secondary">
              <MapPin size={32} className="mb-2" />
              <p className="text-sm">No properties found</p>
              <p className="text-xs mt-1">Try a different search term</p>
            </div>
          )}

          {!loading && !hasSearched && !error && (
            <div className="flex flex-col items-center justify-center py-12 text-theme-secondary">
              <Search size={32} className="mb-2" />
              <p className="text-sm">Search for a property in Aspire</p>
              <p className="text-xs mt-1">Enter a name or leave blank to browse all</p>
            </div>
          )}

          {!loading && properties.length > 0 && (
            <div className="divide-y divide-line">
              {properties.map((property) => (
                <button
                  key={property.PropertyID}
                  onClick={() => handleSelect(property)}
                  className="w-full px-6 py-3.5 text-left hover:bg-teal-50 dark:hover:bg-teal-500/10 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-theme-primary group-hover:text-teal-700 dark:group-hover:text-teal-300 truncate">
                        {property.PropertyName}
                      </p>
                      {property.PropertyAddress && (
                        <p className="text-xs text-theme-secondary mt-0.5 truncate">
                          {property.PropertyAddress}
                        </p>
                      )}
                      {(property.ContactName || property.ContactCompany) && (
                        <p className="text-xs text-theme-secondary mt-0.5">
                          {property.ContactName}
                          {property.ContactCompany ? ` (${property.ContactCompany})` : ''}
                        </p>
                      )}
                      {(property.ContactEmail || property.ContactPhone) && (
                        <p className="text-xs text-theme-secondary mt-0.5">
                          {[property.ContactPhone, property.ContactEmail].filter(Boolean).join(' | ')}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-teal-600 dark:text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 mt-0.5">
                      Select
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-line bg-subtle rounded-b-xl">
          <p className="text-xs text-theme-secondary">
            Select a property to fill the form with its details from Aspire.
          </p>
        </div>
      </div>
    </div>
  );
}
